import React, { useState } from 'react';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

interface RequirePasswordResetModalProps {
  isOpen: boolean;
  userName: string;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
  isLoading?: boolean;
}

const RequirePasswordResetModal: React.FC<RequirePasswordResetModalProps> = ({
  isOpen,
  userName,
  onSubmit,
  isLoading = false
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Todos os campos são obrigatórios');
      return false;
    }

    if (newPassword.length < 8) {
      setError('A palavra-passe deve ter pelo menos 8 caracteres');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('A palavra-passe e a confirmação não coincidem');
      return false;
    }

    // Validar requisitos da palavra-passe
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[@#!$%&*]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setError('A palavra-passe não cumpre todos os requisitos');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao redefinir palavra-passe');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Não permite fechar o modal enquanto estiver submetendo
  const handleClose = () => {
    if (!isSubmitting) {
      // Limpa os campos ao fechar
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Redefinição de Palavra-Passe Obrigatória"
      size="md"
      showCloseButton={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Mensagem informativa */}
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md">
          <div className="flex items-start space-x-2">
            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm">
              <p className="font-medium mb-1">
                Olá, {userName}!
              </p>
              <p>
                Por segurança, é necessário redefinir a sua palavra-passe antes de continuar usando o sistema.
                Por favor, crie uma nova palavra-passe que siga os requisitos de segurança.
              </p>
            </div>
          </div>
        </div>

        {/* Senha Atual */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Palavra-Passe Atual *
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Digite a palavra-passe atual"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Nova Senha */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nova Palavra-Passe *
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Digite a nova palavra-passe"
            disabled={isSubmitting}
            required
            autoFocus
          />
        </div>

        {/* Indicador de Força da Palavra-Passe */}
        {newPassword && (
          <div className="mt-2">
            <PasswordStrengthIndicator password={newPassword} />
          </div>
        )}

        {/* Confirmar Nova Senha */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirmar Nova Palavra-Passe *
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Confirme a nova palavra-passe"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Erro */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Informação adicional */}
        <div className="bg-gray-50 border border-gray-200 text-gray-600 px-3 py-2 rounded-md text-sm">
          <p>
            <strong>Nota:</strong> Esta redefinição é obrigatória e não poderá ser adiada.
            Após definir a sua nova palavra-passe, você poderá continuar usando o sistema normalmente.
          </p>
        </div>

        {/* Botões */}
        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isLoading}
            loading={isSubmitting || isLoading}
            className="w-full sm:w-auto"
          >
            {isSubmitting || isLoading ? 'Redefinindo...' : 'Redefinir Palavra-Passe e Continuar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RequirePasswordResetModal;